import { OverlayContainer, PedagogyRoom, PedagogyRoomViewer } from "@kalyzee/escaet-ui";
import { useEffect, useMemo } from "react";
import { useMeetingSelector, useMultiMeetingAPI } from "../../hooks/meeting";
import { EscaetPedagogyAction, MeetingPedagogyUserData } from "../../interfaces/pedagogy";
import MeetingSessionPlayer from "../player/MeetingSessionPlayer";

export interface RoomViewerProps {
  pedagogyRoom?: PedagogyRoom;
  pedagogyUsers: MeetingPedagogyUserData[];
  actions: EscaetPedagogyAction[];
  onClose?: () => void;
}

/**
 * This component subscribes and retrieves the meeting, sessions and video sessions
 * that are needed to display an escaet pedagogy room.
 */

export const RoomViewer = ({ pedagogyRoom, pedagogyUsers, actions, onClose }: RoomViewerProps) => {
  const { pedagogy } = useMultiMeetingAPI(undefined, false);
  const pedaMeetingAPI = pedagogy?.container?.getAPI();

  const [meeting] = useMeetingSelector(pedaMeetingAPI, (state) => state.meeting);
  const [sessions] = useMeetingSelector(pedaMeetingAPI, (state) => state.meeting?.sessions);
  const [videoSessions] = useMeetingSelector(pedaMeetingAPI, (state) => state.receptionSessions);

  const users = useMemo(() => {
    if (!meeting) return undefined;

    const users = pedagogyUsers.filter((u) => u.inWaitingRoom && (!pedagogyRoom || u.roomId === pedagogyRoom.id));
    const usersWithVideo = users.map((u) => {
      const userSession = sessions?.find((s) => s.user?.id === u.id);
      if (!userSession) return u;
      const userVideoSessions = videoSessions?.filter((s) => s.data.session.id === userSession.id);
      if (!userVideoSessions) return u;
      const videoNode = (
        <MeetingSessionPlayer meeting={meeting} sessionId={userSession.id} ovenMediaSessions={userVideoSessions} style={{ width: "100%", height: "100%" }} />
      );
      return {
        ...u,
        videoNode,
      };
    });

    return usersWithVideo;
  }, [meeting, pedagogyRoom, pedagogyUsers]);

  const viewerActions = useMemo(() => actions.filter((a) => ["exclude", "warn", "contact", "tolerate"].includes(a.type)), [actions]);

  useEffect(() => {
    if (!users?.length) {
      onClose?.();
    }
  }, [users]);

  if (!users) return null;

  return (
    <OverlayContainer>
      <PedagogyRoomViewer
        style={{
          width: "90%",
          height: "90%",
        }}
        room={pedagogyRoom}
        users={users}
        actions={viewerActions as any}
        onClose={onClose}
      />
    </OverlayContainer>
  );
};
